// src/auth/RequireAuth.jsx
//
// Route guard for pages that any LOGGED-IN user may see
// (e.g. /user-dashboard, /course-hub, /payments, curriculum pages).
// It does NOT check role or course access — only "is someone signed in".
//
// This replaces two older files that did almost the same thing under
// different names (auth/Dashboard/ProtectedRoute.js and
// auth/GeneralProtectedRoute.js). Having two near-identical guards was
// confusing and one of them redirected to a route ("/login") that
// doesn't exist in this app (the real sign-in route is "/signin").
// Both old files should be deleted once every usage points here.
//
// Usage in App.js:
//   <Route path="/user-dashboard" element={
//     <RequireAuth user={user}><UserDashboard /></RequireAuth>
//   } />

import { Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";

/**
 * @param {object} props
 * @param {import("firebase/auth").User | null | undefined} props.user
 *   - undefined: Firebase hasn't finished checking auth state yet (show a spinner)
 *   - null: Firebase finished checking, nobody is logged in (redirect)
 *   - a user object: logged in (render the protected page)
 * @param {React.ReactNode} props.children
 */
const RequireAuth = ({ user, children }) => {
    const isStillCheckingAuthState = user === undefined;

    if (isStillCheckingAuthState) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
            >
                <CircularProgress />
            </Box>
        );
    }

    const isLoggedIn = Boolean(user);

    if (!isLoggedIn) {
        return <Navigate to="/signin" replace />;
    }

    return children;
};

export default RequireAuth;
